
import type { Mode } from 'vanilla-jsoneditor'
import type { PropType } from 'vue'
import {
  Content,
  JSONContent,
  JSONEditor,
  JSONEditorPropsOptional,
  TextContent,
} from 'vanilla-jsoneditor'
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'

export { Content, JSONContent, JSONEditorPropsOptional, TextContent }

export enum MenuEnum {
  NEVER = 'never',
  ALWAYS = 'always',
  AUTO = 'auto',
}

export default defineComponent({
  name: 'JsonEditor',
  props: {
    menu: {
      type: String as PropType<MenuEnum>,
      default: MenuEnum.AUTO,
    },
    content: {
      type: Object as PropType<Content>,
      default: () => ({}),
    },
    readOnly: Boolean,
    mode: {
      type: String as PropType<Mode>,
      default: 'text',
    },
    onChange: {
      type: Function as PropType<(content: Content, previousContent: Content, status: any) => void>,
      default: () => {},
    },
    onError: {
      type: Function as PropType<
        (error: { name: string; message: string; stack?: string }) => void
      >,
      default: () => {},
    },
    onFocus: {
      type: Function as PropType<() => void>,
      default: () => {},
    },
    onBlur: {
      type: Function as PropType<() => void>,
      default: () => {},
    },
  },
  setup(props) {
    const editorRef = ref<Element>()
    const editorProps = ref({
      mainMenuBar: MenuEnum.ALWAYS === props.menu,
      navigationBar: MenuEnum.ALWAYS === props.menu,
      statusBar: MenuEnum.ALWAYS === props.menu,
    })
    let editor

    const onFocus = (): void => {
      if (MenuEnum.AUTO === props.menu) {
        editorProps.value.mainMenuBar = true
        editorProps.value.navigationBar = true
        editorProps.value.statusBar = true
      }

      props.onFocus()
    }

    const onBlur = (): void => {
      if (MenuEnum.AUTO === props.menu) {
        editorProps.value.mainMenuBar = false
        editorProps.value.navigationBar = false
        editorProps.value.statusBar = false
      }

      props.onBlur()
    }

    const mergeProps = (): JSONEditorPropsOptional => {
      const { menu, ...rest } = props
      return {
        ...rest,
        ...editorProps.value,
        onFocus,
        onBlur,
      }
    }

    watch(
      props,
      () => {
        editor?.updateProps(mergeProps())
      },
      { deep: true },
    )
    watch(
      editorProps.value,
      () => {
        editor?.updateProps(mergeProps())
      },
      { deep: true },
    )

    onMounted(() => {
      editor = new JSONEditor({
        target: editorRef.value,
        props: mergeProps(),
      })
    })

    onBeforeUnmount(() => {
      editor.destroy()
      editor = undefined
    })

    return {
      editorRef,
    }
  },
})
